import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import QRCode from 'react-qr-code';

const C = { green:'#10B981', dark:'#064E3B', navy:'#0D1B2A', grey:'#6B7280', border:'#E5E7EB', bg:'#F0FDF4' };

// Format specialization slugs → readable labels
const SPEC_LABELS = {
  ielts:'IELTS', business_english:'Business English', grammar:'Grammar',
  vocabulary:'Vocabulary', conversation:'Conversation', pronunciation:'Pronunciation',
  kids:'Kids English', exam_prep:'Exam Prep', general:'General English',
};
function fmtSpec(s) { return SPEC_LABELS[s] || s.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase()); }

const CEFR_COLORS = {
  A1:'#64748B', A2:'#64748B',
  B1:'#00B4D8', B2:'#00B4D8',
  C1:'#C9A84C', C2:'#C9A84C',
};

function TutorCard({ tutor, idNumber, tenantName, tenantLogo, tenantColor, flipped, onClick }) {
  const initials = `${tutor.first_name?.[0]||''}${tutor.last_name?.[0]||''}`.toUpperCase();
  const accentColor = tenantColor || C.green;
  const idNum = idNumber || tutor.tutor_id_number || '—';

  return (
    <div onClick={onClick} style={{ width:340, height:215, cursor:'pointer', perspective:1000 }}>
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration:0.6, ease:'easeInOut' }}
        style={{ width:'100%', height:'100%', position:'relative', transformStyle:'preserve-3d' }}
      >
        {/* FRONT */}
        <div style={{
          position:'absolute', width:'100%', height:'100%', borderRadius:16,
          backfaceVisibility:'hidden', WebkitBackfaceVisibility:'hidden',
          background:`linear-gradient(135deg, ${accentColor} 0%, ${C.dark} 100%)`,
          boxShadow:'0 20px 60px rgba(0,0,0,0.25)', overflow:'hidden', padding:24,
          boxSizing:'border-box', display:'flex', flexDirection:'column',
        }}>
          {/* Decorative circles */}
          <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.07)' }} />
          <div style={{ position:'absolute', bottom:-60, left:-20, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.05)' }} />

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16, position:'relative' }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:'2px', color:'rgba(255,255,255,0.5)', textTransform:'uppercase' }}>TUTOR ID</div>
              <div style={{ fontSize:18, fontWeight:900, color:'#fff', marginTop:2 }}>
                {tenantName || 'SpeakIQ'}
              </div>
            </div>
            {tenantLogo
              ? <img src={tenantLogo} alt="logo" style={{ height:36, width:36, objectFit:'contain', borderRadius:6, background:'rgba(255,255,255,0.15)', padding:3 }} />
              : <div style={{ width:36, height:36, borderRadius:8, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#fff', fontSize:16 }}>{(tenantName||'S')[0].toUpperCase()}</div>
            }
          </div>

          {/* Tutor info */}
          <div style={{ display:'flex', alignItems:'center', gap:14, flex:1, position:'relative' }}>
            <div style={{ width:56, height:56, borderRadius:'50%', overflow:'hidden', border:'2.5px solid rgba(255,255,255,0.35)', flexShrink:0 }}>
              {tutor.profile_photo_url || tutor.photo_url
                ? <img src={tutor.profile_photo_url || tutor.photo_url} alt={tutor.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <div style={{ width:'100%', height:'100%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:20, color:'#fff' }}>{initials}</div>
              }
            </div>
            <div>
              <div style={{ fontSize:17, fontWeight:800, color:'#fff', marginBottom:2 }}>{tutor.full_name || `${tutor.first_name} ${tutor.last_name}`}</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginBottom:6 }}>
                {(tutor.specializations||[]).slice(0,2).map(fmtSpec).join(' · ') || 'English Tutor'}
              </div>
              <div style={{ fontSize:10, letterSpacing:'1.5px', color:'rgba(255,255,255,0.5)', textTransform:'uppercase' }}>QUALIFIED TUTOR</div>
            </div>
          </div>

          {/* ID Number */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, padding:'8px 12px', background:'rgba(0,0,0,0.25)', borderRadius:8 }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', letterSpacing:'1px' }}>ID NUMBER</div>
            <div style={{ fontSize:14, fontWeight:800, color:'#fff', letterSpacing:'2px', fontFamily:'monospace' }}>{idNum}</div>
          </div>
        </div>

        {/* BACK */}
        <div style={{
          position:'absolute', width:'100%', height:'100%', borderRadius:16,
          backfaceVisibility:'hidden', WebkitBackfaceVisibility:'hidden',
          transform:'rotateY(180deg)',
          background:'#fff', boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
          padding:'12px 16px', boxSizing:'border-box', display:'flex', flexDirection:'column',
          alignItems:'center', justifyContent:'center', gap:6, overflow:'hidden',
          border:`2px solid ${accentColor}20`,
        }}>
          {/* QR Code */}
          <div style={{ background:'#fff', padding:6, borderRadius:8, border:'2px solid #E5E7EB', textAlign:'center', flexShrink:0 }}>
            <div style={{ fontSize:8, fontWeight:700, letterSpacing:1.5, color:C.grey, marginBottom:4 }}>SCAN TO VERIFY</div>
            <QRCode value={`https://speakiq.uk/verify-tutor/${idNum}`} size={80} fgColor={C.navy} bgColor="#fff" />
          </div>
          {/* Details */}
          <div style={{ width:'100%', fontSize:11 }}>
            {[
              ['Name', tutor.full_name || `${tutor.first_name} ${tutor.last_name}`],
              ['ID', idNum],
              ['Specialization', (tutor.specializations||[]).slice(0,2).map(fmtSpec).join(', ') || 'General English'],
              ['School', tenantName || 'SpeakIQ'],
            ].map(([label, value]) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ color:C.grey }}>{label}</span>
                <span style={{ fontWeight:700, color:C.navy, maxWidth:160, textAlign:'right', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{value}</span>
              </div>
            ))}
          </div>
          {/* Footer */}
          <div style={{ fontSize:9, color:C.grey, textAlign:'center' }}>
            speakiq.uk/verify-tutor/{idNum}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function TutorIDCard() {
  const [idData, setIdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    api.get('/api/tutor/my-id')
      .then(r => setIdData(r.data))
      .catch(() => setIdData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout role="tutor" tenantName={idData?.tenant_name} tenantColor={idData?.tenant_color} tenantLogo={idData?.tenant_logo}>
      <div style={{ maxWidth:600, margin:'0 auto' }}>
        <div style={{ marginBottom:24 }}>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:C.navy }}>My Tutor ID</h2>
          <p style={{ margin:0, fontSize:14, color:C.grey }}>Your official SpeakIQ Tutor ID card. Click the card to flip it.</p>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading your ID card…</div>
        ) : !idData?.has_id ? (
          <div style={{ background:'#fff', borderRadius:16, padding:48, textAlign:'center' }}>
            <div style={{ fontSize:56, marginBottom:16 }}>⏳</div>
            <h3 style={{ margin:'0 0 10px', fontSize:18, fontWeight:700, color:C.navy }}>ID Not Issued Yet</h3>
            <p style={{ color:C.grey, fontSize:14, lineHeight:1.7, margin:'0 0 20px' }}>
              Your tutor ID card will be generated automatically once your application has been approved by the school admin.
            </p>
            <div style={{ background:C.bg, borderRadius:10, padding:16, textAlign:'left', fontSize:13 }}>
              {[
                { label:'Application status', value: idData?.tutor?.application_status || 'pending', highlight:true },
                { label:'Submitted', value: idData?.tutor?.created_at ? new Date(idData.tutor.created_at).toLocaleDateString('en-GB') : '—' },
              ].map(row => (
                <div key={row.label} style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ color:C.grey }}>{row.label}</span>
                  <span style={{ fontWeight:700, color: row.highlight ? C.green : C.navy, textTransform:'capitalize' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Card */}
            <div style={{ display:'flex', justifyContent:'center', marginBottom:24 }}>
              <TutorCard
                tutor={idData.tutor}
                idNumber={idData.tutor_id_number}
                tenantName={idData.tenant_name}
                tenantLogo={idData.tenant_logo}
                tenantColor={idData.tenant_color}
                flipped={flipped}
                onClick={() => setFlipped(f => !f)}
              />
            </div>

            <p style={{ textAlign:'center', fontSize:13, color:C.grey, marginBottom:28 }}>
              {flipped ? '← Click card to see front' : 'Click card to see back →'}
            </p>

            {/* ID details card */}
            <div style={{ background:'#fff', borderRadius:14, padding:24, border:`1px solid ${C.border}` }}>
              <div style={{ fontWeight:700, fontSize:15, color:C.navy, marginBottom:16 }}>ID Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16 }}>
                {[
                  ['ID Number', idData.tutor_id_number],
                  ['Full Name', idData.tutor.full_name],
                  ['Date Approved', idData.approved_at ? new Date(idData.approved_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : '—'],
                  ['School', idData.tenant_name],
                  ['Specializations', (idData.tutor.specializations||[]).map(fmtSpec).join(', ') || 'General English'],
                  ['Status', 'Active'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize:11, color:C.grey, marginBottom:3, textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{value || '—'}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop:20, background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, padding:14, fontSize:13, color:'#1D4ED8' }}>
              💡 <strong>Present this ID</strong> to students and at your school to confirm your verified tutor status.
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
